import { type ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetOrgGatesQuery, useGetUserGatesQuery } from '@features/admin-v2/store/api';
import {
  ArtifactDropOverlay,
  formatDuplicateMessage,
  formatUploadLimitError,
  PendingUploadCard,
  useAdminArtifactUploadConstraints,
  useAdminOrganizationArtifactEventStream,
  useChartWideDropZone,
  useOptimisticUploads,
  useUploadAdminOrganizationArtifacts,
  validateFiles,
} from '@features/patient-artifacts';
import { Chip } from '@jotpsych/ui';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { FeatureGates } from '@services/statsig';
import { ROLES } from '@utils/roleUtils';
import { useSnackbar } from 'notistack';

import { IntakeHubDashboard } from './components/IntakeHubDashboard';
import { IntakeHubServiceEnrollmentControls } from './components/IntakeHubServiceEnrollmentControls';
import { IntakeExperience } from './components/shell/IntakeExperience';
import {
  useEnrollAdminIntakeHubServicesMutation,
  useGetAdminIntakeHubDashboardQuery,
  useGetAdminIntakeHubDocumentsQuery,
} from './store/api';
import { documentInventoryPollingInterval } from './documentPolling';
import { intakeHubDashboardResponseToScenario } from './mappers';
import { hasIntakeV2DevelopmentOverride } from './routing';
import type { IntakeItem, IntakeServiceId } from './types';

const DEFAULT_SELECTED_SERVICES: IntakeServiceId[] = ['jotcred', 'jotbill'];

interface AdminIntakeHubProvider {
  provider_id?: string;
  user_id?: string;
  name?: string;
  email?: string;
  roles?: string[];
}

interface AdminIntakeHubEmbeddedProps {
  organizationId: string;
  providers?: AdminIntakeHubProvider[];
}

function toServiceOption(service: Record<string, unknown>, index: number) {
  const fallbackIds: IntakeServiceId[] = ['jotcred', 'jotbill', 'jotsite'];
  const id =
    typeof service.service_key === 'string'
      ? (service.service_key as IntakeServiceId)
      : fallbackIds[index];

  return {
    id,
    label: typeof service.label === 'string' ? service.label : id,
    description: typeof service.description === 'string' ? service.description : '',
  };
}

function getProviderId(provider: AdminIntakeHubProvider) {
  return provider.user_id || provider.provider_id || '';
}

function getEnrollmentServiceId(enrollment: unknown): IntakeServiceId | null {
  if (!enrollment || typeof enrollment !== 'object') return null;
  const serviceKey = (enrollment as { service_key?: unknown }).service_key;
  return typeof serviceKey === 'string' ? (serviceKey as IntakeServiceId) : null;
}

export function AdminIntakeHubEmbedded({
  organizationId,
  providers = [],
}: AdminIntakeHubEmbeddedProps) {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const folderInputRef = useRef<HTMLInputElement | null>(null);
  const pendingUploadItemRef = useRef<IntakeItem | undefined>(undefined);
  const { enqueueSnackbar } = useSnackbar();
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedServiceIds, setSelectedServiceIds] =
    useState<IntakeServiceId[]>(DEFAULT_SELECTED_SERVICES);
  const [uploadingFileCount, setUploadingFileCount] = useState(0);
  const [hasUnsavedV2Changes, setHasUnsavedV2Changes] = useState(false);
  const forceV2 = hasIntakeV2DevelopmentOverride(location.search);

  const {
    data: userGates,
    isFetching: isUserGateFetching,
    error: userGateError,
  } = useGetUserGatesQuery(
    { userId: selectedUserId, organizationId },
    { skip: forceV2 || !organizationId || !selectedUserId },
  );
  const {
    data: organizationGates,
    isFetching: isOrganizationGateFetching,
    error: orgGateError,
  } = useGetOrgGatesQuery(
    { organizationId },
    { skip: forceV2 || !organizationId || Boolean(selectedUserId) },
  );
  const targetGates = selectedUserId ? userGates?.gates : organizationGates?.gates;
  const targetGateEnabled =
    targetGates?.find(gate => gate.gate_id === FeatureGates.INTAKE_HUB_V2)?.evaluated_result ??
    false;
  const isTargetGateFetching = selectedUserId ? isUserGateFetching : isOrganizationGateFetching;
  const targetGateError = selectedUserId ? userGateError : orgGateError;
  // Support escape hatch: the gate's v1 user list is hand-maintained, so a
  // v1 user missing from it evaluates to V2. The override lets admins view
  // either experience for the selected target without editing the gate.
  const [experienceOverride, setExperienceOverride] = useState<'auto' | 'v1' | 'v2'>('auto');
  useEffect(() => {
    setExperienceOverride('auto');
  }, [organizationId, selectedUserId]);
  const isResolvingExperience = !forceV2 && experienceOverride === 'auto' && isTargetGateFetching;
  const showV2 =
    forceV2 || (experienceOverride === 'auto' ? targetGateEnabled : experienceOverride === 'v2');
  const versionDecisionWarning = Boolean(targetGateError) && experienceOverride === 'auto';

  const dashboardArgs = {
    organizationId,
    userId: selectedUserId || undefined,
    ...(showV2 && selectedUserId ? { experience: 'v2' as const } : {}),
  };
  // The header's Start intake / Assign service controls need enrollment and
  // service data in both V1 and V2 — scoped to the selected provider, with
  // `experience: 'v2'` so it shares a cache entry with IntakeExperience's own
  // fetch. In V2 with no provider selected (the aggregate org scope), those
  // controls don't render, so skip the fetch — V2's own dashboard covers that
  // scope itself. `currentData` (not `data`) drives branch selection so a
  // provider switch never renders against the previous provider's payload.
  const { data, currentData, error, isFetching, refetch } = useGetAdminIntakeHubDashboardQuery(
    dashboardArgs,
    {
      skip: !organizationId || isResolvingExperience || (showV2 && !selectedUserId),
    },
  );
  // Polls the document inventory while a document is still being read (see
  // `documentPolling.ts`) so a missed artifact event can't strand the list.
  const [documentPollingInterval, setDocumentPollingInterval] = useState(0);
  const {
    data: documentInventory,
    fulfilledTimeStamp: documentInventoryFetchedAt,
    refetch: refetchDocumentInventory,
  } = useGetAdminIntakeHubDocumentsQuery(dashboardArgs, {
    skip: !organizationId || !data || showV2,
    pollingInterval: documentPollingInterval,
    skipPollingIfUnfocused: true,
  });
  const [enrollServices, { isLoading: isEnrolling }] = useEnrollAdminIntakeHubServicesMutation();
  const { constraints: uploadConstraints, summary: uploadConstraintsSummary } =
    useAdminArtifactUploadConstraints();
  const { upload: uploadOrganizationFiles } = useUploadAdminOrganizationArtifacts(organizationId);
  const { pendingUploads, trackUpload, handleArtifactEvent, handleDocRefExtraction, reconcile } =
    useOptimisticUploads();
  useEffect(() => {
    reconcile(
      (documentInventory?.documents ?? []).map(documentItem => documentItem.document_reference),
    );
  }, [documentInventory?.documents, reconcile]);
  // Re-evaluated on every fetch (not just on data changes) so the age cutoff
  // in `documentInventoryPollingInterval` can end polling for a document that
  // never leaves `pending`.
  useEffect(() => {
    setDocumentPollingInterval(
      documentInventoryPollingInterval(documentInventory?.documents, pendingUploads),
    );
  }, [documentInventory?.documents, documentInventoryFetchedAt, pendingUploads]);

  const providerOptions = useMemo(
    () =>
      providers
        .filter(provider => getProviderId(provider))
        .map(provider => ({
          id: getProviderId(provider),
          label: provider.name || provider.email || getProviderId(provider),
          email: provider.email,
        })),
    [providers],
  );
  const eligibleProviders = useMemo(
    () =>
      providers
        .filter(provider => provider.roles?.includes(ROLES.CLINICIAN))
        .filter(provider => getProviderId(provider))
        .map(provider => ({
          userId: getProviderId(provider),
          name: provider.name || provider.email || getProviderId(provider),
        })),
    [providers],
  );
  const serviceOptions = useMemo(
    () =>
      (data?.services ?? [])
        .filter(Boolean)
        .map((service, index) => toServiceOption(service as Record<string, unknown>, index)),
    [data?.services],
  );
  const enrolledServiceIds = useMemo(
    () =>
      new Set(
        (data?.enrollments ?? [])
          .map(enrollment => getEnrollmentServiceId(enrollment))
          .filter((serviceId): serviceId is IntakeServiceId => Boolean(serviceId)),
      ),
    [data?.enrollments],
  );
  const assignableServiceOptions = useMemo(
    () => serviceOptions.filter(service => !enrolledServiceIds.has(service.id)),
    [enrolledServiceIds, serviceOptions],
  );
  const assignableServiceIds = useMemo(
    () => new Set(assignableServiceOptions.map(service => service.id)),
    [assignableServiceOptions],
  );
  const selectedAssignableServiceIds = useMemo(
    () => selectedServiceIds.filter(serviceId => assignableServiceIds.has(serviceId)),
    [assignableServiceIds, selectedServiceIds],
  );
  const scenario = useMemo(() => intakeHubDashboardResponseToScenario(data), [data]);
  // currentData (not data) here: on a provider switch, RTKQ keeps `data` at
  // the PREVIOUS provider's payload until the new fetch lands, but
  // `currentData` is undefined for that window. hasNoEnrollments gates which
  // branch renders (start-intake card vs the v2 experience), so it must be
  // computed off the target that's actually loaded — matching how
  // IntakeExperience itself reads this same query (currentData, not data).
  const hasNoEnrollments = Boolean(currentData) && (currentData?.enrollments?.length ?? 0) === 0;
  const scopeLabel = selectedUserId
    ? (providerOptions.find(provider => provider.id === selectedUserId)?.label ??
      'Selected provider')
    : 'All providers';
  const showAssignServices =
    Boolean(selectedUserId) && !hasNoEnrollments && assignableServiceOptions.length > 0;
  const showStartIntake = Boolean(selectedUserId) && hasNoEnrollments;

  useEffect(() => {
    folderInputRef.current?.setAttribute('webkitdirectory', '');
    folderInputRef.current?.setAttribute('directory', '');
  }, []);

  const handleUploadFiles = useCallback(
    async (files: File[], item?: IntakeItem) => {
      if (!organizationId) {
        enqueueSnackbar('Select an organization before uploading intake files.', {
          variant: 'warning',
        });
        return;
      }
      if (!selectedUserId) {
        enqueueSnackbar('Select a provider before uploading intake documents.', {
          variant: 'warning',
        });
        return;
      }

      const { accepted, rejected } = validateFiles(files, uploadConstraints);
      for (const rejection of rejected) {
        enqueueSnackbar(rejection.reason, { variant: 'error' });
      }
      if (accepted.length === 0) return;

      setUploadingFileCount(accepted.length);
      let outcome: Awaited<ReturnType<typeof uploadOrganizationFiles>>;
      try {
        outcome = await uploadOrganizationFiles(accepted, {
          sourceChannel: 'intake_hub_admin',
          intakeHub: item
            ? {
                intakeItemId: item.intakeItemId ?? item.id,
                requirementKey: item.requirementKey,
                conceptKey: item.conceptKey,
                serviceKey: item.serviceIds[0],
                userId: selectedUserId,
              }
            : { userId: selectedUserId },
        });
      } catch (uploadError) {
        enqueueSnackbar(formatUploadLimitError(uploadError), { variant: 'error' });
        return;
      } finally {
        setUploadingFileCount(0);
      }

      trackUpload(outcome);
      for (const duplicate of outcome?.duplicates ?? []) {
        enqueueSnackbar(formatDuplicateMessage(duplicate, 'organization'), {
          variant: 'info',
        });
      }
      if (outcome?.artifacts?.length) {
        enqueueSnackbar('Upload received. Intake Hub is extracting the details now.', {
          variant: 'success',
        });
      }
      await refetch();
      await refetchDocumentInventory();
    },
    [
      enqueueSnackbar,
      organizationId,
      refetch,
      refetchDocumentInventory,
      selectedUserId,
      trackUpload,
      uploadConstraints,
      uploadOrganizationFiles,
    ],
  );

  const handleOpenFilePicker = (item?: IntakeItem) => {
    pendingUploadItemRef.current = item;
    fileInputRef.current?.click();
  };

  const handleOpenFolderPicker = () => {
    pendingUploadItemRef.current = undefined;
    folderInputRef.current?.click();
  };

  const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    void handleUploadFiles(files, pendingUploadItemRef.current);
    pendingUploadItemRef.current = undefined;
  };

  const handleArtifactStreamEvent = useCallback(
    (event: Parameters<typeof handleArtifactEvent>[0]) => {
      handleArtifactEvent(event);
      void refetch();
      void refetchDocumentInventory();
    },
    [handleArtifactEvent, refetch, refetchDocumentInventory],
  );

  useAdminOrganizationArtifactEventStream(organizationId, {
    enabled: Boolean(organizationId) && pendingUploads.length > 0,
    onEvent: handleArtifactStreamEvent,
    onDocRefExtraction: event => {
      handleDocRefExtraction(event);
      void refetch();
      void refetchDocumentInventory();
    },
  });

  const { isDragging } = useChartWideDropZone({
    enabled: Boolean(organizationId) && Boolean(selectedUserId) && !hasNoEnrollments && !showV2,
    onDrop: files => void handleUploadFiles(files),
  });
  const isUploadInFlight = uploadingFileCount > 0;

  const handleToggleService = (serviceId: IntakeServiceId) => {
    setSelectedServiceIds(current =>
      current.includes(serviceId)
        ? current.filter(currentServiceId => currentServiceId !== serviceId)
        : [...current, serviceId],
    );
  };

  const handleEnrollSelectedServices = async () => {
    const serviceKeys = hasNoEnrollments ? selectedServiceIds : selectedAssignableServiceIds;
    if (!organizationId || serviceKeys.length === 0) return;
    if (!selectedUserId) {
      enqueueSnackbar('Select a user before assigning Intake Hub services.', {
        variant: 'warning',
      });
      return;
    }
    try {
      await enrollServices({
        organizationId,
        userId: selectedUserId,
        enrollServicesRequest: {
          service_keys: serviceKeys,
        },
      }).unwrap();
    } catch {
      enqueueSnackbar('Unable to update Intake Hub services right now. Please try again.', {
        variant: 'error',
      });
      return;
    }
    try {
      await refetch();
      await refetchDocumentInventory();
    } catch {
      // Refresh is best-effort: in v2 the document-inventory query is
      // skipped and RTK Query's refetch() throws on a never-started query.
      // The enrollment above already succeeded, so a refresh problem must
      // not be reported as a save failure.
    }
    setSelectedServiceIds(current =>
      hasNoEnrollments ? current : current.filter(serviceId => !serviceKeys.includes(serviceId)),
    );
    enqueueSnackbar(hasNoEnrollments ? 'Intake started.' : 'Service assigned.', {
      variant: 'success',
    });
  };

  // Shared between the v1 and v2 branches below: when a specific provider
  // has zero enrollments, both versions show this same start-intake card
  // (the v2 form has nothing to render without at least one enrollment).
  const startIntakeCard = (
    <Box sx={{ p: { xs: 1.5, md: 2 } }}>
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6">Start intake for {scopeLabel}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Select service enrollments, then Intake Hub will create the required items for this
                organization or provider scope.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {serviceOptions.map(service => (
                <Chip
                  key={service.id}
                  clickable
                  size="medium"
                  color={selectedServiceIds.includes(service.id) ? 'primary' : 'default'}
                  variant={selectedServiceIds.includes(service.id) ? 'filled' : 'outlined'}
                  label={service.label}
                  onClick={() => handleToggleService(service.id)}
                />
              ))}
            </Stack>
            <Box>
              <Button
                variant="contained"
                disabled={isEnrolling || isFetching || selectedServiceIds.length === 0}
                onClick={() => void handleEnrollSelectedServices()}
              >
                Start intake
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
  // Gate the fallback (v1) and v2 branches alike during an in-flight
  // dashboard fetch for the selected provider. hasNoEnrollments now reads
  // currentData (undefined mid-flight), so without this gate BOTH branches
  // would momentarily fall through to their non-start-intake-card path —
  // v2 to a broken empty IntakeExperience, v1 to the fallback dashboard —
  // whenever a provider switch's request is still in flight. `showV2` stays
  // in the check so v2's semantics are unchanged (it never depended on
  // isFetching); `isFetching` is what makes the v1 switch-between-two-
  // unenrolled-providers window resolve to the loading indicator too,
  // instead of briefly firing the chart-wide drop zone / dashboard render
  // below (~:347) against a stale hasNoEnrollments read.
  const isResolvingDashboard =
    Boolean(selectedUserId) && !currentData && !error && (showV2 || isFetching);

  return (
    <Box sx={{ bgcolor: theme.palette.grey[100], borderRadius: 1, overflow: 'hidden' }}>
      <Box
        component="input"
        ref={fileInputRef}
        type="file"
        multiple
        accept={uploadConstraints.allowed_content_types.join(',')}
        onChange={handleFileInputChange}
        sx={{ display: 'none' }}
      />
      <Box
        component="input"
        ref={folderInputRef}
        type="file"
        multiple
        accept={uploadConstraints.allowed_content_types.join(',')}
        onChange={handleFileInputChange}
        sx={{ display: 'none' }}
      />
      {(isDragging || isUploadInFlight) && (
        <ArtifactDropOverlay
          patientName="Intake Hub"
          subtitle={uploadConstraintsSummary}
          isUploading={isUploadInFlight}
          uploadingMessage={`Uploading ${uploadingFileCount} file${
            uploadingFileCount === 1 ? '' : 's'
          }`}
        />
      )}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: theme.zIndex.appBar,
          bgcolor: alpha(theme.palette.background.paper, 0.9),
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          backdropFilter: 'blur(14px)',
        }}
      >
        {isFetching && <LinearProgress />}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          sx={{
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            px: { xs: 1.5, md: 2 },
            py: 1.25,
          }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography variant="body2" sx={{ fontWeight: 850 }}>
              Intake Hub
            </Typography>
            <Chip
              size="small"
              variant="soft"
              color={error ? 'warning' : 'success'}
              label={error ? 'Needs attention' : 'Admin'}
              sx={{ fontWeight: 800 }}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 260 } }}>
              <InputLabel id="admin-intake-provider-scope-label" shrink>
                Scope
              </InputLabel>
              <Select
                labelId="admin-intake-provider-scope-label"
                label="Scope"
                displayEmpty
                renderValue={value =>
                  value
                    ? (providerOptions.find(provider => provider.id === value)?.label ?? value)
                    : 'All providers'
                }
                value={selectedUserId}
                onChange={event => {
                  if (
                    hasUnsavedV2Changes &&
                    !window.confirm('Discard unsaved Intake Hub changes and change scope?')
                  ) {
                    return;
                  }
                  setSelectedUserId(event.target.value);
                }}
              >
                <MenuItem value="">All providers</MenuItem>
                {providerOptions.map(provider => (
                  <MenuItem key={provider.id} value={provider.id}>
                    <Box>
                      <Typography variant="body2">{provider.label}</Typography>
                      {provider.email && (
                        <Typography variant="caption" color="text.secondary">
                          {provider.email}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              size="small"
              variant="outlined"
              disabled={!selectedUserId}
              onClick={() => handleOpenFilePicker()}
            >
              Upload files
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={!selectedUserId}
              onClick={handleOpenFolderPicker}
            >
              Upload folder
            </Button>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={experienceOverride}
              onChange={(_event, value: 'auto' | 'v1' | 'v2' | null) => {
                if (!value || value === experienceOverride) return;
                const nextShowV2 =
                  forceV2 || (value === 'auto' ? targetGateEnabled : value === 'v2');
                if (
                  showV2 &&
                  !nextShowV2 &&
                  hasUnsavedV2Changes &&
                  !window.confirm('Discard unsaved Intake Hub changes and switch views?')
                ) {
                  return;
                }
                setExperienceOverride(value);
              }}
              aria-label="Intake experience version"
            >
              <ToggleButton value="auto" aria-label="Follow the target's feature gate">
                Auto
              </ToggleButton>
              <ToggleButton value="v1" aria-label="Force the V1 intake view">
                V1
              </ToggleButton>
              <ToggleButton value="v2" aria-label="Force the V2 intake view">
                V2
              </ToggleButton>
            </ToggleButtonGroup>
            <Button size="small" variant="text" onClick={() => navigate('/admin-v2/intake-hub')}>
              Manage definitions
            </Button>
            <Button
              size="small"
              variant="text"
              startIcon={<RefreshOutlinedIcon />}
              onClick={() => void refetch()}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>

        {(showStartIntake || showAssignServices) && (
          <Stack
            direction="row"
            sx={{
              px: { xs: 1.5, md: 2 },
              pb: 1.25,
              borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
              pt: 1,
            }}
          >
            <IntakeHubServiceEnrollmentControls
              mode={showStartIntake ? 'start' : 'assign'}
              scopeLabel={scopeLabel}
              serviceOptions={showStartIntake ? serviceOptions : assignableServiceOptions}
              selectedServiceIds={
                showStartIntake ? selectedServiceIds : selectedAssignableServiceIds
              }
              onToggleService={handleToggleService}
              onSubmit={() => void handleEnrollSelectedServices()}
              isSubmitting={isEnrolling}
              isDisabled={isFetching}
            />
          </Stack>
        )}
      </Box>

      {pendingUploads.length > 0 && (
        <Box
          sx={{
            position: 'sticky',
            // The header grows by one row when the Start intake / Assign
            // service controls render, so the pending-upload overlay needs
            // to sit lower to avoid overlapping them.
            top: showStartIntake || showAssignServices ? 116 : 72,
            zIndex: theme.zIndex.appBar - 1,
            px: { xs: 1.5, md: 2 },
            pt: 2,
            pointerEvents: 'none',
          }}
        >
          <Stack spacing={0.75} sx={{ pointerEvents: 'auto' }}>
            {pendingUploads.map(upload => (
              <PendingUploadCard
                key={upload.artifactId}
                filename={upload.filename}
                status={upload.status}
                documentReferences={upload.documentReferences}
                extractionSignals={upload.extractionSignals}
              />
            ))}
          </Stack>
        </Box>
      )}

      {versionDecisionWarning && (
        <Alert severity="warning">
          The target Intake Hub version could not be confirmed. The V1 admin view remains available.
        </Alert>
      )}

      {isResolvingExperience || isResolvingDashboard ? (
        <LinearProgress aria-label="Loading target Intake Hub experience" />
      ) : showV2 && selectedUserId && hasNoEnrollments ? (
        startIntakeCard
      ) : showV2 ? (
        <IntakeExperience
          key={`${organizationId}:${selectedUserId || 'organization'}`}
          context={{
            kind: 'admin',
            organizationId,
            userId: selectedUserId || undefined,
            providerEmail: providerOptions.find(provider => provider.id === selectedUserId)?.email,
            targetLabel: providerOptions.find(provider => provider.id === selectedUserId)?.label,
            eligibleProviders,
          }}
          onDirtyChange={setHasUnsavedV2Changes}
        />
      ) : (
        <IntakeHubDashboard
          scenario={scenario}
          documentInventory={documentInventory?.documents ?? []}
          documentAccess={{
            scope: 'admin',
            organizationId,
            userId: selectedUserId || undefined,
          }}
          onUploadRequest={selectedUserId ? handleOpenFilePicker : undefined}
        />
      )}
    </Box>
  );
}
